import { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import Layout from "./Layout";
import { PageHeader, StatusBadge, EmptyState } from "./Shared";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Plus, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CrudPage({
  title, subtitle, endpoint, exportEntity, fields, columns, emptyIcon, testidPrefix,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dynOpts, setDynOpts] = useState({});

  const load = async () => {
    try { const res = await api.get(`/${endpoint}`); setItems(res.data); }
    catch (e) { toast.error("Yükleme hatası"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    fields.filter((f) => f.optionsEndpoint).forEach(async (f) => {
      try {
        const res = await api.get(`/${f.optionsEndpoint}`);
        setDynOpts((prev) => ({
          ...prev,
          [f.name]: res.data.map((o) => ({ value: o[f.optionValue || "id"], label: o[f.optionLabel || "name"] })),
        }));
      } catch (e) {}
    });
  }, []);

  const openNew = () => {
    const init = {};
    fields.forEach((f) => { init[f.name] = f.default !== undefined ? f.default : ""; });
    setForm(init); setEditing(null); setOpen(true);
  };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item); setOpen(true); };

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = parseFloat(payload[f.name]) || 0;
      if (payload[f.name] === "__none__") payload[f.name] = null;
    });
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false); load();
    } catch (e) { toast.error("Kaydetme hatası"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/${endpoint}/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error("Silme hatası"); }
  };

  const doExport = async () => {
    try {
      const res = await api.get(`/reports/export?entity=${exportEntity}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${exportEntity}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV indirildi");
    } catch (e) { toast.error("Dışa aktarma hatası"); }
  };

  return (
    <Layout>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex gap-2">
            <Button variant="outline" data-testid={`${testidPrefix}-export`} onClick={doExport} className="gap-2">
              <DownloadSimple size={18} /> CSV
            </Button>
            <Button data-testid={`${testidPrefix}-add`} onClick={openNew} className="gap-2">
              <Plus size={18} weight="bold" /> Yeni Ekle
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={emptyIcon} title="Kayıt bulunamadı" desc="İlk kaydınızı eklemek için 'Yeni Ekle' butonunu kullanın." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid={`${testidPrefix}-table`}>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                {columns.map((c) => (
                  <TableHead key={c.key} className={`text-xs uppercase tracking-[0.15em] ${c.align === "right" ? "text-right" : ""}`}>{c.label}</TableHead>
                ))}
                <TableHead className="text-right text-xs uppercase tracking-[0.15em]">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="border-border" data-testid={`${testidPrefix}-row`}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.align === "right" ? "text-right font-mono" : ""}>
                      {c.render ? c.render(item) : item[c.key]}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`${testidPrefix}-edit`} onClick={() => openEdit(item)} className="p-2 text-muted-foreground hover:text-primary transition-colors duration-200"><PencilSimple size={18} /></button>
                      <button data-testid={`${testidPrefix}-delete`} onClick={() => remove(item.id)} className="p-2 text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash size={18} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border" data-testid={`${testidPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Düzenle" : "Yeni Kayıt"}</DialogTitle>
            <DialogDescription>Alanları doldurup kaydedin.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2 space-y-2" : "space-y-2"}>
                <Label>{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger data-testid={`${testidPrefix}-field-${f.name}`}><SelectValue placeholder={f.label} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {f.allowEmpty && <SelectItem value="__none__">— Yok —</SelectItem>}
                      {(f.options || dynOpts[f.name] || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`${testidPrefix}-field-${f.name}`}
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.label}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid={`${testidPrefix}-save`} onClick={save}>{editing ? "Güncelle" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export { fmtMoney, StatusBadge };
